/**
 * Meta anual — quantos livros contam para o ano.
 *
 * ## O bug que este módulo existe para fechar
 *
 * A meta era medida por `user_stats.totalBooksRead`, que é o total **da vida**
 * do usuário. O bloco dizia "Meta 2026" e mostrava um número que incluía livro
 * lido em 2023, livro importado do Goodreads e livro sem data nenhuma. Bastava
 * catalogar uma estante antiga para a meta do ano nascer batida — medido em
 * 28/07 pelo fundador: meta de 2026 cumprida com livros de 2023.
 *
 * O contador vitalício não estava errado; estava respondendo outra pergunta.
 * "Quantos livros você já leu" e "quantos você leu ESTE ano" são coisas
 * diferentes, e só a segunda tem a ver com uma promessa datada.
 *
 * ## Por que isto é client-side
 *
 * `totalBooksRead` vem do servidor e é barato, mas não sabe de ano nenhum —
 * é um contador incremental. A contagem por ano precisa olhar `finishedAt` de
 * cada livro, e isso mora na estante. Fazer no client tem duas vantagens que
 * pagam a troca: sai de graça (a estante já está em memória, via listener) e é
 * **reativo** — corrigir a data de um livro move o número da meta no mesmo
 * instante, sem nenhuma recontagem no servidor.
 *
 * ⚠️ **Teto conhecido:** a contagem vê só os livros carregados. O
 * `UserBooksContext` assina a primeira página (`subscribeToUserBooksFirstPage`)
 * e cresce conforme o usuário pagina. Estante grande com muitos lidos no mesmo
 * ano pode subcontar até o resto carregar. É o mesmo teto que a Home já vivia
 * desde sempre (ela filtra por ano em cima do mesmo contexto), e pré-beta
 * ninguém tem estante desse tamanho — mas quando alguém tiver, a saída é uma
 * count query server-side, não um contador denormalizado (que voltaria a
 * ignorar edição retroativa de data, que é justamente o caso de uso novo).
 */

import type { UserBook } from "../../../services/firebase/booksService";
import { toMs } from "../../../utils/timestampUtils";
import { currentYearLocal, yearOfLocal } from "./finishedDate";

export { currentYearLocal };

/**
 * Ano em que o livro foi concluído, ou `null` quando ele não tem data.
 *
 * `null` é um estado NORMAL e frequente, não um defeito a consertar: o
 * onboarding grava a declaração de leituras antigas sem data de propósito, a
 * importação de CSV raramente traz uma, e desde 01/08 marcar "Lido" sem
 * declarar dia também deixa o campo vazio em vez de inventar hoje.
 */
export function finishedYearOf(book: UserBook): number | null {
  const ms = toMs(book.finishedAt);
  return ms > 0 ? yearOfLocal(ms) : null;
}

/** O livro conta para a meta de `year`? Exige status "read" E data no ano. */
export function countsForYear(book: UserBook, year: number): boolean {
  return book.status === "read" && finishedYearOf(book) === year;
}

/**
 * Livros que contam para a meta do ano.
 *
 * Aceita a estante inteira (não só os lidos) porque os chamadores têm as duas
 * formas em mãos e filtrar duas vezes é mais barato que errar o filtro.
 */
export function countBooksFinishedInYear(books: UserBook[], year: number): number {
  return books.reduce((n, b) => (countsForYear(b, year) ? n + 1 : n), 0);
}
